import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MatDialogRef} from "@angular/material/dialog";
import {LicenseAddDialogComponent} from "../license-add-dialog/license-add-dialog.component";
import {Observable, of, ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {AuthService} from "../../../shared/services/auth.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormHelperService} from "../../../shared/services/form-helper.service";
import {LicenseService} from "../../../shared/services/license.service";
import {License} from "../../../shared/interface/license";
import {delay, distinctUntilChanged, first, takeUntil, tap} from "rxjs/operators";
import {Router} from "@angular/router";
import {Uuid} from "../../../shared/interface/response/uuid";
import {HttpErrorResponse} from "@angular/common/http";
import {User} from "../../../shared/interface/user";
import {DYNAMIC_DICTIONARY_LICENSE_TYPE} from "../../../shared/services/dict.service";

@Component({
  selector: 'app-license-add',
  templateUrl: './license-add.component.html',
  styleUrls: ['./license-add.component.scss']
})
export class LicenseAddComponent implements OnInit, OnDestroy {
  readonly DYNAMIC_DICTIONARY_LICENSE_TYPE = DYNAMIC_DICTIONARY_LICENSE_TYPE;

  @Input() userObject?: User;
  @Input() dialogRef?: MatDialogRef<LicenseAddDialogComponent>;
  @Input() license?: Observable<License>;
  @Output() closeModification = new EventEmitter<any>();
  @Output() licenseCreated = new EventEmitter<string>();

  licenseForm: FormGroup;
  processingRequest = false;
  errors: any = {};
  userPermissions: ReplaySubject<UserPermissions>;
  licenseObject: License;

  selectedUser: User | null = null;

  licenseNumberControl: FormControl;
  typeOfLicenseControl: FormControl;
  userControl: FormControl;
  deviceControl: FormControl;
  noteControl: FormControl;
  expirationDateControl: FormControl;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private initialized: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private formHelper: FormHelperService,
    private licenseService: LicenseService,
    private router: Router,
    private snackBar: MatSnackBar,
  ) {
    this.userPermissions = this.auth.getUserPermissions();
    this.licenseForm = fb.group({
      licenseNumber: this.licenseNumberControl = fb.control('', [Validators.required]),
      typeOfLicense: this.typeOfLicenseControl = fb.control('', [Validators.required]),
      user: this.userControl = fb.control('', [Validators.required]),
      device: this.deviceControl = fb.control(''),
      note: this.noteControl = fb.control(''),
      expirationDate: this.expirationDateControl = fb.control(''),
    });
  }

  ngOnInit(): void {
    this.userControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged()
      )
      .subscribe((userId: string) => {
        if (userId) {
          this.selectedUser = { uuid: userId } as User;
          this.deviceControl.reset();
        } else {
          this.selectedUser = null;
          this.deviceControl.reset();
        }
      });

    if (this.license) {
      this.license
        .pipe(takeUntil(this.destroyed$))
        .subscribe((license: License) => {
          this.licenseObject = license;

          if (license?.user?.uuid) {
            this.selectedUser = license.user;
          }

          this.initialize(license);
        });
    } else {
      this.initialized.next(true);
    }

    if (this.userObject) {
      this.userControl.setValue(this.userObject.uuid || this.userObject);
      this.userControl.disable();
      this.selectedUser = this.userObject;
    }
  }

  initialize(licenseObject: License): void {
    of(licenseObject)
      .pipe(
        delay(0),
        tap(() => {
          this.licenseForm.reset({
            typeOfLicense: licenseObject?.typeOfLicense?.id,
            licenseNumber: licenseObject?.licenseNumber,
            expirationDate: licenseObject?.expirationDate,
            user: licenseObject.user?.uuid || licenseObject.user,
            device: licenseObject?.device?.uuid,
            note: licenseObject?.note,
          });

          this.initialized.next(true);
        })
      ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  save(): void {
    this.processingRequest = true;
    this.licenseService.sendForm(
      this.licenseForm.getRawValue(),
      this.licenseObject ? this.licenseObject.uuid : null
    )
      .pipe(
        first()
      )
      .subscribe((response: Uuid) => {
        this.snackBar.open(this.licenseObject ? 'Pomyślnie zaktualizowano licencję' : 'Pomyślnie utworzono licencję', '', {
          duration: 2000,
        });
        this.processingRequest = false;

        if (this.dialogRef) {
          this.licenseCreated.emit();
          this.dialogRef.close();
        } else if (this.license) {
          this.closeModification.emit();
        } else {
          this.router.navigate(['/licencje/szczegoly', response.uuid]);
        }
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

        this.snackBar.open('Podczas przesyłania formularza wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.errors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.licenseForm, this.errors);
        }
      });
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.closeModification.emit();
    }
  }
}
